import { createContext, useContext, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSession, signOut } from '@/lib/auth-client'

interface User {
  id: string
  email: string
  name: string
  accountID: string
  role: 'admin' | 'user'
  image?: string
  emailVerified: boolean
}

interface AuthContextType {
  user: User | null
  loading: boolean
  error: string | null
  logout: () => Promise<void>
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const { data: session, isPending, error } = useSession()
  const navigate = useNavigate()

  const logout = async () => {
    try {
      await signOut()
      navigate('/login')
    } catch (error) {
      console.error('Logout error:', error)
      navigate('/login')
    }
  }

  // Map Better Auth user to our User type. accountID/role are app-specific fields not present
  // on Better Auth's base user type, so read them through a narrow extension type.
  const sessionUser = session?.user
  const extra = sessionUser as { accountID?: string; role?: 'admin' | 'user' } | undefined
  const user: User | null = sessionUser ? {
    id: sessionUser.id,
    email: sessionUser.email,
    name: sessionUser.name,
    accountID: extra?.accountID ?? sessionUser.id, // fallback to id if no accountID
    role: extra?.role ?? 'user', // default to 'user' if no role
    image: sessionUser.image ?? undefined,
    emailVerified: sessionUser.emailVerified ?? false,
  } : null

  const value = {
    user,
    loading: isPending,
    error: error?.message ?? null,
    logout,
    isAuthenticated: !!session?.user,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

// Idiomatic context pattern: the hook lives with its provider.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
